/**
 * The field picker — a chip showing the chosen field, opening a searchable,
 * grouped list of EVERY field this workspace has: Profile, Your columns,
 * Derived, Activity. Ported from the concept's `segments/FieldPicker.jsx`.
 *
 * ## WHAT CHANGED FROM THE CONCEPT, AND WHY
 *
 * - **The list is the catalogue, not a sample.** `GET /fields` answers with
 *   every column of every ready upload, keyed by its name verbatim. A workspace
 *   whose sheets carry "Joining date" and "Loyalty tier" sees exactly those, and
 *   nothing here knows any column's name in advance.
 * - **Coverage can be absent.** A workspace with no people has no coverage at
 *   all, and `coverage: null` draws `CoverageMeter`'s "not measured" rather than
 *   a 0% bar — a share of nobody is not zero.
 * - **A date nobody has read yet asks its question here.** A CSV date column
 *   holding 03/04/2026 is two different days depending on who exported it, and
 *   Wend never guesses (`needsDateFormat`). Picking one opens three answers —
 *   day first, month first, year first — inline, and the choice is saved as
 *   the field's setting before the rule uses it. Without a handler to save it
 *   (a read-only use), the row says where to answer instead of selecting a
 *   field every rule over it would refuse.
 * - **The group tag is the catalogue's group**, drawn with `FIELD_CHIP_CLASS`
 *   and a `Tag` rather than `FieldChip`, whose groups are the concept's sample
 *   groups (orders, reach, consent) — a "columns" chip there would print a group
 *   the kit does not have.
 *
 * Keyboard: the search box takes focus on open; ↓ moves into the list, ↑/↓
 * walk it, ↑ from the first item returns to the search, Enter in the search
 * picks the first match, Escape closes and returns focus to the chip, and
 * tabbing out closes it the same as clicking elsewhere.
 *
 * Coverage is said, not hovered: the picker's foot states what the bar is a
 * share of, and the "latest upload wins" rule is a visible line rather than a
 * `title` tooltip (page-hardening refuses prose in tooltips).
 */
import {
  FIELD_DATE_FORMAT_LABEL,
  FIELD_DATE_FORMAT_VALUES,
  FIELD_GROUP_LABEL,
  FIELD_GROUP_VALUES,
  LATEST_UPLOAD_WINS,
  type Field,
  type FieldDateFormat,
  type FieldGroup,
  type FieldType,
} from '@wend/shared';
import { CoverageMeter, FIELD_CHIP_CLASS, Tag, cn, focusRing, type TagTone } from '@wend/ui';
import { ChevronDown, Search } from 'lucide-react';
import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';

/** The tag tone of each catalogue group — identity colours, never statuses. */
export const FIELD_GROUP_TAG_TONE: Readonly<Record<FieldGroup, TagTone>> = {
  profile: 'neutral',
  columns: 'ident-a',
  derived: 'brand',
  activity: 'ident-d',
};

/** The word in the chip's tag: one short noun per group. */
export const FIELD_GROUP_TAG: Readonly<Record<FieldGroup, string>> = {
  profile: 'Profile',
  columns: 'Column',
  derived: 'Derived',
  activity: 'Activity',
};

/** A type, as the picker prints it — the concept's words. */
export const FIELD_TYPE_LABEL: Readonly<Record<FieldType, string>> = {
  text: 'text',
  number: 'number',
  date: 'date',
  enum: 'choice',
  boolean: 'yes / no',
  event: 'event',
};

/** 0–1 coverage → the meter's percent, or null when nothing was measured. */
export function coveragePercent(field: Pick<Field, 'coverage'>): number | null {
  return field.coverage === null ? null : field.coverage * 100;
}

/** A read-only chip: the group tag and the field's label. */
export function FieldTag({ field, fallbackKey }: { field: Pick<Field, 'group' | 'label'> | undefined; fallbackKey?: string }) {
  return (
    <span className={FIELD_CHIP_CLASS}>
      {field !== undefined ? (
        <Tag inset tone={FIELD_GROUP_TAG_TONE[field.group]}>
          {FIELD_GROUP_TAG[field.group]}
        </Tag>
      ) : (
        <Tag inset tone="neutral">
          Missing
        </Tag>
      )}
      <span className="min-w-0 truncate">{field?.label ?? fallbackKey ?? 'Unknown field'}</span>
    </span>
  );
}

export interface FieldPickerProps {
  /** The fields on offer — normally the catalogue less hidden ones. */
  readonly fields: readonly Field[];
  /** The chosen key. May name a field the catalogue no longer has. */
  readonly value: string | null;
  readonly onChange: (key: string) => void;
  /**
   * Saves a date format for a field whose dates are ambiguous, then the picker
   * selects it. Absent: such a field is listed but not selectable, with a line
   * saying where to answer.
   */
  readonly onChooseDateFormat?: (key: string, format: FieldDateFormat) => Promise<void>;
  /** Accessible name of the chip; default "Field". */
  readonly label?: string;
  readonly disabled?: boolean;
  /** Restrict to some types — a derived field's source, a flow condition. */
  readonly types?: readonly FieldType[];
}

export function FieldPicker({ fields, value, onChange, onChooseDateFormat, label = 'Field', disabled = false, types }: FieldPickerProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [asking, setAsking] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [askError, setAskError] = useState<string | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const panelId = useId();

  const chosen = value === null ? undefined : fields.find((field) => field.key === value);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (event: MouseEvent) => {
      if (wrapRef.current !== null && event.target instanceof Node && !wrapRef.current.contains(event.target)) close(false);
    };
    document.addEventListener('mousedown', onDown);
    const timer = window.setTimeout(() => searchRef.current?.focus(), 0);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.clearTimeout(timer);
    };
    // `close` only sets state; re-subscribing on its identity would only churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function close(refocus = true): void {
    setOpen(false);
    setQuery('');
    setAsking(null);
    setAskError(null);
    if (refocus) buttonRef.current?.focus();
  }

  function choose(field: Field): void {
    if (field.needsDateFormat) {
      setAsking(field.key);
      return;
    }
    onChange(field.key);
    close();
  }

  async function answerFormat(field: Field, format: FieldDateFormat): Promise<void> {
    if (onChooseDateFormat === undefined) return;
    setSaving(true);
    setAskError(null);
    try {
      await onChooseDateFormat(field.key, format);
      onChange(field.key);
      close();
    } catch {
      setAskError('That did not save. Try again, or set it on the Fields page.');
    } finally {
      setSaving(false);
    }
  }

  const needle = query.trim().toLowerCase();
  const offered = fields.filter(
    (field) =>
      !field.hidden &&
      (types === undefined || types.includes(field.type)) &&
      (needle === '' ||
        field.label.toLowerCase().includes(needle) ||
        field.key.toLowerCase().includes(needle) ||
        FIELD_GROUP_LABEL[field.group].toLowerCase().includes(needle)),
  );

  function onListKey(event: KeyboardEvent<HTMLDivElement>): void {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    event.preventDefault();
    const items = [...(wrapRef.current?.querySelectorAll<HTMLButtonElement>('[data-field-option]') ?? [])];
    const index = items.findIndex((item) => item === document.activeElement);
    if (event.key === 'ArrowUp' && index <= 0) {
      searchRef.current?.focus();
      return;
    }
    items[event.key === 'ArrowDown' ? Math.min(items.length - 1, index + 1) : index - 1]?.focus();
  }

  return (
    <div
      ref={wrapRef}
      className="relative min-w-0"
      onKeyDown={(event) => {
        if (event.key === 'Escape' && open) {
          event.stopPropagation();
          close();
        }
      }}
      onBlur={(event) => {
        if (open && event.relatedTarget instanceof Node && wrapRef.current !== null && !wrapRef.current.contains(event.relatedTarget)) close(false);
      }}
    >
      <button
        type="button"
        ref={buttonRef}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        aria-label={`${label}: ${chosen?.label ?? value ?? 'none chosen'}. Change field`}
        onClick={() => (open ? close(false) : setOpen(true))}
        data-role="field-picker"
        className={cn(
          FIELD_CHIP_CLASS,
          'cursor-pointer hover:bg-brand-a/24 disabled:cursor-not-allowed disabled:opacity-60',
          open && 'bg-brand-a/24',
          focusRing,
        )}
      >
        {chosen !== undefined ? (
          <Tag inset tone={FIELD_GROUP_TAG_TONE[chosen.group]}>
            {FIELD_GROUP_TAG[chosen.group]}
          </Tag>
        ) : (
          <Tag inset tone="neutral">
            {value === null ? 'Field' : 'Missing'}
          </Tag>
        )}
        <span className="min-w-0 truncate">{chosen?.label ?? value ?? 'Choose a field'}</span>
        <ChevronDown aria-hidden="true" className="size-3.5 shrink-0 text-brand-ink" />
      </button>

      {open && (
        <div
          id={panelId}
          role="dialog"
          aria-label="Choose a field"
          className="absolute left-0 top-[calc(100%+6px)] z-30 w-[min(400px,84vw)] overflow-hidden rounded-base border border-brand-b/40 bg-surface-2 shadow-[0_24px_60px_-16px_rgba(0,0,0,0.85)] animate-pop motion-reduce:animate-none"
        >
          <div className="flex items-center gap-2 border-b border-line px-3 py-2 text-muted focus-within:shadow-[inset_0_-2px_0_var(--color-brand-b)]">
            <Search aria-hidden="true" className="size-4 shrink-0" />
            <input
              ref={searchRef}
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => {
                const first = offered[0];
                if (event.key === 'Enter' && first !== undefined) {
                  event.preventDefault();
                  choose(first);
                }
                if (event.key === 'ArrowDown') {
                  event.preventDefault();
                  wrapRef.current?.querySelector<HTMLButtonElement>('[data-field-option]')?.focus();
                }
              }}
              placeholder={`Search ${String(offered.length)} fields…`}
              aria-label="Search fields"
              className="min-w-0 flex-1 bg-transparent text-[0.86rem] text-text outline-none placeholder:text-muted"
            />
          </div>

          <div className="max-h-[340px] overflow-y-auto overscroll-contain py-1" onKeyDown={onListKey}>
            {FIELD_GROUP_VALUES.map((group) => {
              const items = offered.filter((field) => field.group === group);
              if (items.length === 0) return null;
              return (
                <div key={group} role="group" aria-label={FIELD_GROUP_LABEL[group]}>
                  <p className="flex items-baseline gap-2 px-3 pt-2.5 pb-1 font-mono text-[0.6rem] uppercase tracking-[0.14em] text-brand-ink">
                    <span>{FIELD_GROUP_LABEL[group]}</span>
                    {group === 'derived' && <em className="not-italic normal-case tracking-normal text-muted">computed by Wend</em>}
                    {group === 'columns' && <em className="not-italic normal-case tracking-normal text-muted">from your uploads</em>}
                  </p>
                  {items.map((field) => {
                    const blocked = field.needsDateFormat && onChooseDateFormat === undefined;
                    return (
                      <div key={field.key}>
                        <button
                          type="button"
                          data-field-option=""
                          aria-current={field.key === value ? 'true' : undefined}
                          aria-disabled={blocked || undefined}
                          onClick={() => {
                            if (!blocked) choose(field);
                          }}
                          className={cn(
                            'grid w-full cursor-pointer grid-cols-[minmax(0,1fr)_auto_76px] items-center gap-2.5 px-3 py-1.5 text-left',
                            'hover:bg-brand-a/16 focus-visible:bg-brand-a/16 focus-visible:outline-none',
                            field.key === value && 'bg-brand-a/24',
                            blocked && 'cursor-not-allowed opacity-70',
                          )}
                        >
                          <span className="flex min-w-0 flex-col">
                            <span className="truncate text-[0.85rem] text-text">{field.label}</span>
                            {field.needsDateFormat ? (
                              <span className="font-mono text-[0.64rem] text-warn">
                                {blocked ? 'Dates need a format — set it on the Fields page' : 'Dates need a format — choose one to use it'}
                              </span>
                            ) : field.origin.kind === 'derived' ? (
                              <span className="truncate font-mono text-[0.64rem] text-brand-ink">from {field.origin.sourceField}</span>
                            ) : null}
                          </span>
                          <span className="whitespace-nowrap rounded-xs border border-line px-1.5 py-0.5 font-mono text-[0.62rem] text-muted">
                            {FIELD_TYPE_LABEL[field.type]}
                          </span>
                          <CoverageMeter percent={coveragePercent(field)} basis="known" compact />
                        </button>

                        {asking === field.key && (
                          <div className="mx-3 mb-2 rounded-sm border border-warn/35 bg-warn/8 p-2.5" role="group" aria-label={`How ${field.label} is written`}>
                            <p className="mb-2 text-[0.76rem] text-warn-ink">
                              {field.label} holds dates like 03/04/2026. Which way round are they? Wend never guesses.
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                              {FIELD_DATE_FORMAT_VALUES.map((format) => (
                                <button
                                  key={format}
                                  type="button"
                                  disabled={saving}
                                  onClick={() => {
                                    void answerFormat(field, format);
                                  }}
                                  className={cn(
                                    'cursor-pointer rounded-sm border border-line-strong bg-black/30 px-2 py-1 text-[0.74rem] text-text hover:border-brand-b/60 disabled:opacity-60',
                                    focusRing,
                                  )}
                                >
                                  {FIELD_DATE_FORMAT_LABEL[format]}
                                </button>
                              ))}
                            </div>
                            {askError !== null && (
                              <p role="alert" className="mt-1.5 text-[0.74rem] text-bad">
                                {askError}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
            {offered.length === 0 && (
              <p className="px-3 py-3 text-[0.8rem] text-muted">
                {needle === ''
                  ? 'This workspace has no fields yet. Upload a list and every column becomes one.'
                  : `No field called “${query.trim()}” in this workspace yet. Upload a file with that column and it appears here.`}
              </p>
            )}
          </div>

          <p className="border-t border-line px-3 py-2 text-[0.7rem] leading-snug text-muted">
            The bar is the share of people who have a value. {LATEST_UPLOAD_WINS}
          </p>
        </div>
      )}
    </div>
  );
}
